import { TGetExamStepListResDto } from "@/api/services/main/exam-steps/dtos/get-exam-step-list.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PROCEDURE_STATUS, TExamLearnersListReqDto, TExamLearnersResDto } from "@/api/services/main/exam-steps/dtos/get-exam-step-learners.dto";
import { TUpdateLearnerProcedureStatusReqDto, TUpdateLearnerProcedureStatusResDto } from "@/api/services/main/exam-steps/dtos/update-learner-procedure-status.dto";
import { TUpdateLearnerRequirementStatusReqDto } from "@/api/services/main/exam-steps/dtos/update-learner-requirement-status.dto";

export const fetchExamStepIndustriesApi = async (): Promise<TGetExamStepListResDto> => {
  const { data } = await clientApi.get<TGetExamStepListResDto>("/exam/industries");
  return data;
};

export const useExamStepIndustries = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.EXAM_STEPS.FETCH_INDUSTRIES],
    queryFn: () => fetchExamStepIndustriesApi(),
  });
};

export const getExamLearnersApi = async (
  params: TExamLearnersListReqDto
): Promise<TExamLearnersResDto> => {
  const { industryId, ...query } = params;
  const { data } = await clientApi.get<TExamLearnersResDto>(
    `/exam-procedure-status/industry/${industryId}/learners`,
    { params: query }
  );
  return data;
};

export const useExamStepLearners = (params: TExamLearnersListReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.EXAM_STEPS.FETCH_LEARNERS, params],
    queryFn: () => getExamLearnersApi(params),
    enabled: params.industryId > 0,
  });
};

export const updateLearnerProcedureStatusApi = async (
  params: TUpdateLearnerProcedureStatusReqDto
): Promise<TUpdateLearnerProcedureStatusResDto> => {
  const { industryId, learnerId, key } = params;
  const { data } = await clientApi.patch<TUpdateLearnerProcedureStatusResDto>(
    `/exam-procedure-status/industry/${industryId}/learners/${learnerId}/status`,
    { key }
  );
  return data;
};

export const updateLearnerRequirementStatusApi = async (
  params: TUpdateLearnerRequirementStatusReqDto
): Promise<void> => {
  const { industryId, learnerId, requirementId, checked } = params;
  await clientApi.patch(
    `/exam-procedure-status/industry/${industryId}/learners/${learnerId}/exam-requirement/${requirementId}`,
    { checked }
  );
};

export const useUpdateLearnerRequirementStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: TUpdateLearnerRequirementStatusReqDto) => updateLearnerRequirementStatusApi(params),
    onSuccess: (_data, variables) => {
      // BE は単一要件のトグル + 空ボディ。variables から該当 learner の
      // examRequirementIds をトグルして全キャッシュ変種に反映する。
      const queries = queryClient.getQueriesData<TExamLearnersResDto>({
        queryKey: [QUERY_KEYS.EXAM_STEPS.FETCH_LEARNERS],
      });
      for (const [queryKey, oldData] of queries) {
        if (!oldData) continue;
        if (!oldData.data.some((l) => l.id === variables.learnerId)) continue;
        queryClient.setQueryData(queryKey, {
          ...oldData,
          data: oldData.data.map((learner) => {
            if (learner.id !== variables.learnerId) return learner;
            const current = learner.examRequirementIds ?? [];
            const examRequirementIds = variables.checked
              ? Array.from(new Set([...current, variables.requirementId]))
              : current.filter((id) => id !== variables.requirementId);
            return { ...learner, examRequirementIds };
          }),
        });
      }
    },
  });
};

// carries the constant `IN_PROGRESS`. Normalize so any NEXT:* status matches the filter group.
const normalizeStatusForFilter = (key: string): string => (key.startsWith("NEXT:") ? PROCEDURE_STATUS.IN_PROGRESS : key);

export const useUpdateLearnerProcedureStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: TUpdateLearnerProcedureStatusReqDto) =>
      updateLearnerProcedureStatusApi(params),
    onSuccess: (data, variables) => {
      // Per cached variant: patch the row if the new status matches the variant's procedureStatuses
      // filter, otherwise drop it + decrement pagination.
      const queries = queryClient.getQueriesData<TExamLearnersResDto>({
        queryKey: [QUERY_KEYS.EXAM_STEPS.FETCH_LEARNERS],
      });
      for (const [queryKey, oldData] of queries) {
        if (!oldData) continue;
        if (!oldData.data.some((l) => l.id === variables.learnerId)) continue;

        const params = (queryKey as unknown[])[1] as TExamLearnersListReqDto | undefined;
        const visibleStatuses = params?.procedureStatuses;
        const normalizedKey = normalizeStatusForFilter(variables.key);
        const stillVisible = !visibleStatuses || visibleStatuses.length === 0 || visibleStatuses.includes(normalizedKey);

        let newData: TExamLearnersResDto;
        if (stillVisible) {
          newData = {
            ...oldData,
            data: oldData.data.map((learner) =>
              learner.id === variables.learnerId
                ? {
                    ...learner,
                    currentStatus: data.currentStatus,
                    isLocked: data.isLocked,
                    nextExamProcedureId: data.nextExamProcedureId,
                    nextProcedureDuration: data.nextProcedureDuration,
                    nextProcedureStepName: data.nextProcedureStepName,
                    nextProcedureEndDate: data.nextProcedureEndDate,
                    statusOptions: data.statusOptions,
                  }
                : learner,
            ),
          };
        } else {
          const filteredData = oldData.data.filter((l) => l.id !== variables.learnerId);
          const newTotal = Math.max(0, oldData.pagination.total - 1);
          const perPage = oldData.pagination.perPage || 1;
          newData = {
            ...oldData,
            data: filteredData,
            pagination: {
              ...oldData.pagination,
              total: newTotal,
              count: filteredData.length,
              totalPage: Math.max(1, Math.ceil(newTotal / perPage)),
            },
          };
        }
        queryClient.setQueryData(queryKey, newData);
      }
    },
  });
};
